import type { ProviderNotice } from './types';

export const DEFAULT_TOUR_TIMES = [
  '9:00 AM',
  '10:00 AM',
  '11:00 AM',
  '1:00 PM',
  '2:30 PM',
  '4:00 PM',
] as const;

export const buildTourDates = (now: Date = new Date(), count = 14): Date[] => {
  const start = new Date(now);
  start.setHours(12, 0, 0, 0);
  start.setDate(start.getDate() + 1);

  return Array.from({ length: count }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return date;
  });
};

export const formatTourDateLabel = (date: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(date);

export const isTourDateClosed = (date: Date): boolean => date.getDay() === 0 || date.getDay() === 6;

const timeToMinutes = (value: string): number | null => {
  const match = value.match(/\b(\d{1,2})(?::(\d{2}))?\s*(AM|PM)\b/i);
  if (!match) return null;

  let hour = Number(match[1]);
  const minute = Number(match[2] || 0);
  const period = match[3].toUpperCase();
  if (hour === 12) hour = 0;
  if (period === 'PM') hour += 12;
  return hour * 60 + minute;
};

export const getNoticeForDate = (date: Date, notices: ProviderNotice[]): ProviderNotice | null =>
  notices.find((notice) => notice.dateLabel === formatTourDateLabel(date)) ?? null;

export const getAvailableTourTimes = (
  date: Date,
  notices: ProviderNotice[],
  baseTimes: readonly string[] = DEFAULT_TOUR_TIMES,
): string[] => {
  if (isTourDateClosed(date)) return [];

  const matching = notices.filter((notice) => notice.dateLabel === formatTourDateLabel(date));
  if (matching.some((notice) => /\bclosed\b/i.test(notice.message))) return [];

  return baseTimes.filter((time) => {
    const slotMinutes = timeToMinutes(time);
    if (slotMinutes === null) return false;

    return matching.every((notice) => {
      const noticeMinutes = timeToMinutes(notice.message);
      if (noticeMinutes === null) return true;
      if (/\bclos(?:e|ing)\b/i.test(notice.message)) return slotMinutes < noticeMinutes;
      if (/\bopen(?:s|ing)?\b/i.test(notice.message)) return slotMinutes >= noticeMinutes;
      return true;
    });
  });
};
